import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardList } from "lucide-react";

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export default function StocktakeDialog({ open, onOpenChange, onSaved }) {
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState({});
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows({});
    setSearch("");
    http
      .get("/products")
      .then((r) => setProducts((r.data || []).filter((p) => p.track_stock)))
      .catch(() => toast.error("Σφάλμα φόρτωσης ειδών"));
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) =>
        (p.description || "").toLowerCase().includes(q) ||
        String(p.code || "").toLowerCase().includes(q)
    );
  }, [products, search]);

  const setRow = (pid, field, value) =>
    setRows((prev) => ({ ...prev, [pid]: { ...prev[pid], [field]: value } }));

  // Νέα Παραλαβή σε αρνητικό απόθεμα: το νέο απόθεμα ξεκινά από το 0.
  const previewStock = (p) => {
    const r = rows[p.id] || {};
    const cur = num(p.stock_qty);
    let val = r.set_stock !== undefined && r.set_stock !== "" ? num(r.set_stock) : cur;
    const rec = r.receive_qty !== undefined && r.receive_qty !== "" ? num(r.receive_qty) : 0;
    if (rec) val = (val > 0 ? val : 0) + rec;
    return val;
  };

  const isChanged = (p) => previewStock(p) !== num(p.stock_qty);
  const changedCount = products.filter(isChanged).length;

  const save = async () => {
    const payloadRows = [];
    for (const p of products) {
      const r = rows[p.id];
      if (!r) continue;
      const row = { product_id: p.id };
      if (r.set_stock !== undefined && r.set_stock !== "" && num(r.set_stock) !== num(p.stock_qty)) {
        row.set_stock = num(r.set_stock);
      }
      if (r.receive_qty !== undefined && r.receive_qty !== "" && num(r.receive_qty) !== 0) {
        row.receive_qty = num(r.receive_qty);
      }
      if (row.set_stock !== undefined || row.receive_qty !== undefined) payloadRows.push(row);
    }
    if (!payloadRows.length) {
      toast.info("Δεν υπάρχουν αλλαγές για αποθήκευση");
      return;
    }
    setSaving(true);
    try {
      const res = await http.post("/products/stocktake", { rows: payloadRows });
      toast.success(`Απογραφή: ενημερώθηκαν ${res.data.updated} είδη`);
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error("Σφάλμα αποθήκευσης απογραφής");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="stocktake-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList size={20} className="text-emerald-600" /> Απογραφή Αποθέματος
          </DialogTitle>
        </DialogHeader>
        <Input
          placeholder="Αναζήτηση είδους..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="stocktake-search"
          className="max-w-xs"
        />
        <div className="flex-1 overflow-y-auto border border-slate-200 rounded-md">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-100 z-10">
              <tr className="text-left text-xs text-slate-600">
                <th className="px-3 py-2">Είδος</th>
                <th className="px-3 py-2 w-36 text-right">Υπάρχον Απόθεμα</th>
                <th className="px-3 py-2 w-36 text-right">Νέα Παραλαβή</th>
                <th className="px-3 py-2 w-28 text-right">Νέο Απόθεμα</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const r = rows[p.id] || {};
                const cur = num(p.stock_qty);
                const preview = previewStock(p);
                const changed = preview !== cur;
                return (
                  <tr key={p.id} className={`border-t border-slate-100 ${changed ? "bg-emerald-50/60" : ""}`}>
                    <td className="px-3 py-1.5">
                      <div className="font-bold text-slate-800">{p.description}</div>
                      <div className="text-[11px] text-slate-400 font-mono">
                        #{p.code}
                        {cur < 0 && (
                          <span className="ml-2 text-red-600 font-bold">αρνητικό απόθεμα</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-1.5">
                      <Input
                        type="number"
                        step="0.01"
                        value={r.set_stock !== undefined ? r.set_stock : cur}
                        onChange={(e) => setRow(p.id, "set_stock", e.target.value)}
                        data-testid={`stocktake-set-${p.id}`}
                        className={`h-9 text-right font-mono ${cur < 0 && (r.set_stock === undefined || r.set_stock === "") ? "text-red-600 font-bold" : ""}`}
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0"
                        value={r.receive_qty ?? ""}
                        onChange={(e) => setRow(p.id, "receive_qty", e.target.value)}
                        data-testid={`stocktake-receive-${p.id}`}
                        className="h-9 text-right font-mono"
                      />
                    </td>
                    <td
                      className={`px-3 py-1.5 text-right font-mono font-bold ${
                        preview <= 0 ? "text-red-600" : changed ? "text-emerald-700" : "text-slate-700"
                      }`}
                      data-testid={`stocktake-preview-${p.id}`}
                    >
                      {Number.isInteger(preview) ? preview : preview.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-slate-400">
                    Δεν βρέθηκαν είδη με ενεργό «Έλεγχο αποθέματος»
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <DialogFooter className="items-center gap-3">
          <span className="text-xs text-slate-500 mr-auto">
            {changedCount > 0 ? `${changedCount} είδη με αλλαγές` : "Καμία αλλαγή"} · Σε αρνητικό
            απόθεμα η παραλαβή ξεκινά από το 0
          </span>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="stocktake-cancel">
            Άκυρο
          </Button>
          <Button
            onClick={save}
            disabled={saving || changedCount === 0}
            data-testid="stocktake-save"
            className="bg-emerald-600 hover:bg-emerald-700 font-bold"
          >
            {saving ? "Αποθήκευση..." : "Αποθήκευση Απογραφής"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
